import {Observable} from 'rxjs';
import {Http, URLSearchParams} from '@angular/http';
import {Injectable} from '@angular/core';
import {ProfileState, ProfileType} from '../entity';
import {CustomerProfile, VerificationStatus} from '../entity/customer-profile';
import {HttpUtils} from '../../../../../../../common/utils';
import {PageableResponse} from '../../../../../../../common/entity';
import {ProfileKind} from '../../../../../shared/api/entity/profile-kind';
import {UpdateCustomerProfile} from '../../../../../shared/api';
import {ProductCode} from "../../../../../shared/api/entity/product-code";

export type CustomerProfilesFilter = {
    id: number;
    customerName: string;
    email: string;
    introducerName: string;
    accountTypeId: number;
    period: { beginDate: string, endDate: string };
};

@Injectable()
export class CustomerProfileService {

    constructor(private _http: Http) {}

    getCustomerProfile(id: number): Observable<CustomerProfile> {
        return this._http.get(`/api/backoffice/customers?id=${id}`)
            .map(response => response.json().records[0]);
    }

    searchCustomerProfileById(id: number): Observable<CustomerProfile> {
        return this._http.get(`/api/backoffice/customers/${id}`)
        .map(response => response.json());
    }

    getNewCustomerProfile(id: number): Observable<CustomerProfile> {
        return this._http.get(`/api/backoffice/customers?new&id=${id}`)
            .map(response => response.json().records[0]);
    }

    getProcessingCustomerProfile(id: number): Observable<CustomerProfile> {
        return this._http.get(`/api/backoffice/customers?processing&id=${id}`)
            .map(response => response.json().records[0]);
    }

    getDeclinedCustomerProfile(id: number): Observable<CustomerProfile> {
        return this._http.get(`/api/backoffice/customers?declined&id=${id}`)
            .map(response => response.json().records[0]);
    }

    getPendingCustomerProfile(id: number): Observable<CustomerProfile> {
        return this._http.get(`/api/backoffice/customers?pending&id=${id}`)
            .map(response =>  response.json().records[0]);
    }

    findCustomerProfile(id: number, status : string): Observable<CustomerProfile> {
        return this._http.get(`/api/backoffice/customers/profiles/${status}?id=${id}`)
            .map(response =>  response.json().records[0]);
    }

    getNewCustomerProfiles(filter: CustomerProfilesFilter, pagination: { page: number, limit: number }): Observable<PageableResponse<CustomerProfile>> {
        let search: URLSearchParams = HttpUtils.searchParams()
            .append('id', filter.id)
            .append('customerName', filter.customerName)
            .append('email', filter.email)
            .append('introducerName', filter.introducerName)
            .append('accountTypeId', filter.accountTypeId)
            .append('accountOpeningDateFrom', filter.period && filter.period.beginDate)
            .append('accountOpeningDateTo', filter.period && filter.period.endDate)
            .append('page', pagination.page)
            .append('limit', pagination.limit)
            .toSearch();

        return this._http.get('/api/backoffice/customers?new', {search: search})
            .map(response => response.json());
    }

    getDeclinedCustomerProfiles(filter: CustomerProfilesFilter, pagination: { page: number, limit: number }): Observable<PageableResponse<CustomerProfile>> {
        let search: URLSearchParams = HttpUtils.searchParams()
            .append('id', filter.id)
            .append('customerName', filter.customerName)
            .append('email', filter.email)
            .append('introducerName', filter.introducerName)
            .append('accountTypeId', filter.accountTypeId)
            .append('accountOpeningDateFrom', filter.period && filter.period.beginDate)
            .append('accountOpeningDateTo', filter.period && filter.period.endDate)
            .append('page', pagination.page)
            .append('limit', pagination.limit)
            .toSearch();

        return this._http.get('/api/backoffice/customers?declined', {search: search})
            .map(response => response.json());
    }

    getProcessingCustomerProfiles(filter: CustomerProfilesFilter, pagination: { page: number, limit: number }): Observable<PageableResponse<CustomerProfile>> {
        let search: URLSearchParams = HttpUtils.searchParams()
            .append('id', filter.id)
            .append('customerName', filter.customerName)
            .append('email', filter.email)
            .append('introducerName', filter.introducerName)
            .append('accountTypeId', filter.accountTypeId)
            .append('accountOpeningDateFrom', filter.period && filter.period.beginDate)
            .append('accountOpeningDateTo', filter.period && filter.period.endDate)
            .append('page', pagination.page)
            .append('limit', pagination.limit)
            .toSearch();

        return this._http.get('/api/backoffice/customers?processing', {search: search})
            .map(response => response.json());
    }

    getCustomerProfiles(filter: CustomerProfilesFilter, pagination: { page: number, limit: number }, status : string): Observable<PageableResponse<CustomerProfile>> {
        let search: URLSearchParams = HttpUtils.searchParams()
            .append('id', filter.id)
            .append('customerName', filter.customerName)
            .append('email', filter.email)
            .append('introducerName', filter.introducerName)
            .append('accountTypeId', filter.accountTypeId)
            .append('accountOpeningDateFrom', filter.period && filter.period.beginDate)
            .append('accountOpeningDateTo', filter.period && filter.period.endDate)
            .append('page', pagination.page)
            .append('limit', pagination.limit)
            .toSearch();

        return this._http.get(`/api/backoffice/customers/profiles/${status}`, {search: search})
            .map(response => response.json());
    }

    getProfileStatesForRegisteredClients(): Observable<[ProfileState]> {
        return this._http.get('/api/backoffice/customers/registered/states').map(response => response.json());
    }

    getProfileTypes(): Observable<[ProfileType]> {
        return this._http.get('/api/backoffice/customers/types').map(response => response.json());
    }

    getProfileKinds(): Observable<ProfileKind[]> {
        return this._http.get('/api/backoffice/dictionary/profile_kinds')
            .map(response => response.json().records);
    }

    getProductCode(): Observable<ProductCode[]> {
        return this._http.get('/api/backoffice/dictionary/product_code')
            .map(response => response.json().records);
    }

    declineCustomerProfileRegistration(customerId : number, declinedReasonsIds: Array<number>, declinedCustomReason : string){
       return this._http.patch(`/api/backoffice/customers/${customerId}?decline`, { reasonsIds: declinedReasonsIds, customReason: declinedCustomReason}).map(response => response.json());
    }

    closeCustomerProfile (customerId: number, closeReasonsIds: Array<number>, closeCustomReason: string) {
        return this._http.patch(`/api/backoffice/customers/${customerId}?close`, { reasonsIds: closeReasonsIds, customReason: closeCustomReason}).map(response => response);
    }

    suspendCustomerProfile (customerId: number, suspendedReasonsIds: Array<number>, suspendedCustomReason: string) {
        return this._http.patch(`/api/backoffice/customers/${customerId}?suspend`, { reasonsIds: suspendedReasonsIds, customReason: suspendedCustomReason}).map(response => response);
    }

    unSuspendCustomerProfile (customerId: number) {
        return this._http.patch(`/api/backoffice/customers/${customerId}?unsuspend`, {}).map(response => response);
    }

    getProfileVerificationStatuses(): Observable<[VerificationStatus]> {
        return this._http.get('/api/backoffice/customers/verification-statuses').map(response => response.json().items);
    }

    getDeclinedCustomerProfileReasons(id: number): Observable<UpdateCustomerProfile> {
        return this._http.get(`/api/backoffice/customers/${id}?declinedProfile`)
            .map(response => response.json());
    }

    getCustomerProfileReasons(id: number, profileState: string): Observable<UpdateCustomerProfile> {
        return this._http.get(`/api/backoffice/customers/reasons?id=${id}&status=${profileState}`)
            .map(response => response.json());
    }

    processCustomerProfile(customerId: number, productCode: number): Observable<any> {
        const body = {productCode: productCode};
        return this._http.post(`/api/backoffice/customers/${customerId}/process`, body)
            .map(response => response.json())
    }

    processAgentCustomerProfile(customerId: number): Observable<any> {
        return this._http.post(`/api/backoffice/customers/${customerId}/process/agent`, {})
            .map(response => response.json())
    }

    processMoonwardCustomerProfile(customerId: number): Observable<any> {
        return this._http.post(`/api/backoffice/customers/${customerId}/process/moorwand`, {})
            .map(response => response.json())
    }

    processFinXpCustomerProfile(customerId: number): Observable<any> {
        return this._http.post(`/api/backoffice/customers/${customerId}/process/finxp`, {})
            .map(response => response.json())
    }

    processEmoneyCustomerProfile(customerId: number): Observable<any> {
        return this._http.post(`/api/backoffice/customers/${customerId}/process/emoney`, {})
            .map(response => response.json())
    }

    updateCustomerProfile(customerId : number, currentProfileState : string, newProfileState: string): Observable<any>{
        return this._http.post(`/api/backoffice/customers/${customerId}/updateProfileState`,{currentProfileState,newProfileState})
            .map(response => response.json())
     }

     updateCustomerProfileWithReason(customerId : number, status: string, reasonsIds: Array<number>, customReason : string){
        return this._http.patch(`/api/backoffice/customers/${customerId}/updateProfileState`, { newProfileState: status, reasonsIds: reasonsIds, customReason: customReason}).map(response => response.json());
     }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/customers/shared/api/service/customer-profile.service.ts